import { getAccessToken } from "@qw/auth";
import { useMyAccountQuery } from "@qw/graphql";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * Whether LaunchDarkly has finished the per-user identify round-trip started by
 * {@link LaunchDarklyIdentify}. Until this is `true`, flag values still reflect
 * the anonymous bootstrap context, so a `false` flag reading is "unknown, not
 * identified yet" rather than an authoritative "off". Callers gating an
 * irreversible action (e.g. a redirect) on a flag should wait for this.
 */
const LaunchDarklyIdentifiedContext = createContext<boolean>(false);

export const useLaunchDarklyIdentified = (): boolean => useContext(LaunchDarklyIdentifiedContext);

export const LaunchDarklyIdentify = ({ children }: { children?: ReactNode }) => {
  // This component wraps the whole app, so it renders on unauthenticated routes
  // too — including /login. Querying there returns unauthorized, which the Apollo
  // client turns into a session reset plus a redirect to /login, so the reload
  // re-issues the query and the app never escapes the login screen. Only ask for
  // the account once there's a token to ask with.
  const { data } = useMyAccountQuery({ skip: !getAccessToken() });
  const ldClient = useLDClient();
  const [isIdentified, setIsIdentified] = useState(false);

  const user = data?.myAccount;
  const context = useMemo(() => {
    if (!user?.id || !user.email) {
      return null;
    }

    const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

    return {
      kind: "user" as const,
      key: user.id,
      email: user.email,
      ...(name ? { name } : {}),
    };
  }, [user?.email, user?.firstName, user?.id, user?.lastName]);

  // Dedupe on identity (key + email) only. Targeting is by email, so a cosmetic
  // name edit must not re-fire identify; the NUL separator keeps key/email from
  // colliding across the boundary.
  const contextSignature = context ? `${context.key}\u0000${context.email}` : null;
  const lastIdentifiedContextRef = useRef<string | null>(null);
  // Monotonic per-attempt token. A signature comparison can't distinguish two
  // identify() calls for the SAME identity, so across an A -> B -> A flip a
  // still-in-flight identify(A) would settle while its signature is current
  // again and re-open the gate on stale flags. Only the newest attempt settles.
  const identifyGenerationRef = useRef(0);

  useEffect(() => {
    if (!ldClient || !context || !contextSignature || lastIdentifiedContextRef.current === contextSignature) {
      return;
    }

    lastIdentifiedContextRef.current = contextSignature;

    // A new identity (in-place impersonation A -> B) hasn't had its flags
    // re-evaluated yet, so drop back to "not identified" until this identify
    // settles — otherwise a consumer would treat B with A's still-current flag
    // values.
    setIsIdentified(false);
    identifyGenerationRef.current += 1;
    const identifyGeneration = identifyGenerationRef.current;

    // Identify each identity once. We deliberately don't reset-and-retry on
    // failure: a same-value reset can't re-trigger this effect (deps are
    // unchanged), and resetting is unsound across an A→B→A identity flip where a
    // late rejection of the first A could clobber a newer, successful A. A
    // transient failure resolves on the next identity change or a reload.
    //
    // Mark identified once the round-trip settles: on success flag values are
    // now user-scoped; on failure we fall back to the anonymous values we
    // already have. Only the newest attempt may settle the gate, so a stale
    // identify — even one for the same identity across an A -> B -> A flip —
    // can't re-open it on flags that haven't been re-evaluated yet.
    void ldClient
      .identify(context)
      .catch(() => {})
      .finally(() => {
        if (identifyGenerationRef.current === identifyGeneration) {
          setIsIdentified(true);
        }
      });
  }, [context, contextSignature, ldClient]);

  return (
    <LaunchDarklyIdentifiedContext.Provider value={isIdentified}>{children}</LaunchDarklyIdentifiedContext.Provider>
  );
};
